import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    getClientAllergies,
    getClientMedications,
    getClientPrescriptionEvents,
    getClientSyncStatus,
    getDrFirstNotificationCount,
    getLabCategories,
    getLabCompanies,
    getLabCompanyTests,
    getLabRequest,
    getLabRequests,
    getLabTests,
    getPrescriptions,
} from "../services/e-prescribe.services";
import {
    IApiResponse,
    IPaginatedApiResponse,
} from "../../../utils/types/api-response";
import {
    IClientAllergy,
    IClientAllergyListFilters,
    IClientMedication,
    IClientMedicationsListFilters,
    IClientPrescriptionEventListFilters,
    IClientSyncStatusResponse,
    IDrFirstNotificationCountData,
    ILabCategory,
    ILabCategoryListFilters,
    ILabCompany,
    ILabCompanyTest,
    ILabCompanyTestListFilters,
    ILabRequest,
    ILabRequestListFilters,
    ILabTest,
    ILabTestListFilters,
    IPrescription,
    IPrescriptionEvent,
    IPrescriptionListFilters,
} from "../types/e-prescribe.types";
import { handleRemoveEmptyParamFromQueryParams } from "../../../utils/helpers";

export function useGetClientSyncStatus(practiceId: string, clientId: string) {
    return useQuery<
        IApiResponse<IClientSyncStatusResponse>,
        AxiosError<IApiResponse<IClientSyncStatusResponse>>
    >({
        queryKey: ["client-sync-status", clientId],
        queryFn: () => getClientSyncStatus(practiceId, clientId),
    });
}

export function useGetClientAllergies({
    practiceId,
    clientId,
    filters,
    enabled = true,
}: {
    practiceId: string;
    clientId: string;
    filters?: IClientAllergyListFilters;
    enabled?: boolean;
}) {
    return useQuery<
        IApiResponse<IClientAllergy[]>,
        AxiosError<IApiResponse<IClientAllergy[]>>
    >({
        queryKey: [
            "client-allergies",
            clientId,
            handleRemoveEmptyParamFromQueryParams(filters ?? {}),
        ],
        queryFn: () => getClientAllergies(practiceId, clientId, filters),
        enabled,
    });
}

export function useGetClientMedications({
    practiceId,
    clientId,
    filters,
    enabled = true,
}: {
    practiceId: string;
    clientId: string;
    filters?: IClientMedicationsListFilters;
    enabled?: boolean;
}) {
    return useQuery<
        IApiResponse<IClientMedication[]>,
        AxiosError<IApiResponse<IClientMedication[]>>
    >({
        queryKey: [
            "client-medications",
            clientId,
            handleRemoveEmptyParamFromQueryParams(filters ?? {}),
        ],
        queryFn: () => getClientMedications(practiceId, clientId, filters),
        enabled,
    });
}

export function useGetPrescriptions({
    practiceId,
    filters,
    enabled = true,
}: {
    practiceId: string;
    filters?: IPrescriptionListFilters;
    enabled?: boolean;
}) {
    return useQuery<
        IPaginatedApiResponse<IPrescription[]>,
        AxiosError<IPaginatedApiResponse<IPrescription[]>>
    >({
        queryKey: [
            "prescriptions",
            handleRemoveEmptyParamFromQueryParams(filters ?? {}),
        ],
        queryFn: () => getPrescriptions(practiceId, filters),
        enabled,
    });
}

export function useGetClientPrescriptionEvents(
    practiceId: string,
    clientId: string,
    filters?: IClientPrescriptionEventListFilters
) {
    return useQuery<
        IApiResponse<IPrescriptionEvent[]>,
        AxiosError<IApiResponse<IPrescriptionEvent[]>>
    >({
        queryKey: [
            "prescription-events",
            clientId,
            handleRemoveEmptyParamFromQueryParams(filters ?? {}),
        ],
        queryFn: () =>
            getClientPrescriptionEvents(practiceId, clientId, filters),
    });
}

export function useGetDrFirstNotificationCount(
    practiceId: string,
    enabled?: boolean
) {
    return useQuery<
        IApiResponse<IDrFirstNotificationCountData>,
        AxiosError<IApiResponse<IDrFirstNotificationCountData>>
    >({
        queryKey: ["drfirst-notification-count", practiceId],
        queryFn: () => getDrFirstNotificationCount(practiceId),
        enabled,
    });
}

export function useGetLabRequests(filters?: ILabRequestListFilters) {
    return useQuery<
        IPaginatedApiResponse<ILabRequest[]>,
        AxiosError<IPaginatedApiResponse<ILabRequest[]>>
    >({
        queryKey: [
            "lab-requests",
            handleRemoveEmptyParamFromQueryParams(filters ?? {}),
        ],
        queryFn: () => getLabRequests(filters),
    });
}

export function useGetLabCompanies() {
    return useQuery<
        IApiResponse<ILabCompany[]>,
        AxiosError<IApiResponse<ILabCompany[]>>
    >({
        queryKey: ["lab-companies"],
        queryFn: getLabCompanies,
    });
}

export function useGetLabCategories(filters?: ILabCategoryListFilters) {
    return useQuery<
        IApiResponse<ILabCategory[]>,
        AxiosError<IApiResponse<ILabCategory[]>>
    >({
        queryKey: [
            "lab-categories",
            handleRemoveEmptyParamFromQueryParams(filters ?? {}),
        ],
        queryFn: () => getLabCategories(filters),
    });
}

export function useGetLabTests(filters?: ILabTestListFilters) {
    return useQuery<
        IApiResponse<ILabTest[]>,
        AxiosError<IApiResponse<ILabTest[]>>
    >({
        queryKey: [
            "lab-tests",
            handleRemoveEmptyParamFromQueryParams(filters ?? {}),
        ],
        queryFn: () => getLabTests(filters),
    });
}

export function useGetLabCompanyTests(
    filters?: ILabCompanyTestListFilters,
    enabled = true
) {
    return useQuery<
        IApiResponse<ILabCompanyTest[]>,
        AxiosError<IApiResponse<ILabCompanyTest[]>>
    >({
        queryKey: [
            "lab-company-tests",
            handleRemoveEmptyParamFromQueryParams(filters ?? {}),
        ],
        queryFn: () => getLabCompanyTests(filters),
        enabled,
    });
}

export function useGetLabRequest(labRequestId: string) {
    return useQuery<
        IApiResponse<ILabRequest>,
        AxiosError<IApiResponse<ILabRequest>>
    >({
        queryKey: ["lab-request", labRequestId],
        queryFn: () => getLabRequest(labRequestId),
        enabled: Boolean(labRequestId),
    });
}
